import { useState, useEffect } from 'react';
import { reportsApi, ReportResponse, PersonnelReportData } from '../../api/reports.api';
import { companiesApi, Company } from '../../api/companies.api';
import { departmentsApi, Department } from '../../api/departments.api';
import { personnelApi, Personnel } from '../../api/personnel.api';
import Button from '../../components/forms/Button';
import Select from '../../components/forms/Select';
import Loading from '../../components/common/Loading';
import PersonnelReport from './components/PersonnelReport';
import { generatePersonnelReportPDF } from '../../utils/pdfGenerator';

const PersonnelReportPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<ReportResponse<PersonnelReportData> | null>(null);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [personnel, setPersonnel] = useState<Personnel[]>([]);

  const [personnelId, setPersonnelId] = useState<string>('all');
  const [companyId, setCompanyId] = useState<string>('all');
  const [departmentId, setDepartmentId] = useState<string>('all');

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const [companiesData, departmentsData, personnelData] = await Promise.all([
        companiesApi.getAll(),
        departmentsApi.getAll(),
        personnelApi.getAll(),
      ]);
      setCompanies(companiesData.filter(c => c.isActive).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
      setDepartments(departmentsData.filter(d => d.isActive).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
      setPersonnel(personnelData.filter(p => p.isActive).sort((a, b) => 
        `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, 'tr')
      ));
    } catch (err: any) {
      console.error('Filtre seçenekleri yüklenirken hata:', err);
    }
  };

  const handleGenerateReport = async () => {
    setIsLoading(true);
    setError(null);
    setReportData(null);

    try {
      const filters: any = {};
      if (personnelId && personnelId !== 'all') filters.personnelId = personnelId;
      if (companyId && companyId !== 'all') filters.companyId = companyId;
      if (departmentId && departmentId !== 'all') filters.departmentId = departmentId;

      const data = await reportsApi.getPersonnelReport(filters);
      setReportData(data);
    } catch (err: any) {
      setError(err.message || 'Rapor oluşturulurken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearFilters = () => {
    setPersonnelId('all');
    setCompanyId('all');
    setDepartmentId('all');
    setReportData(null);
  };

  const handleExportPDF = () => {
    if (reportData) {
      generatePersonnelReportPDF(reportData);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Personel Bazlı Cihaz Raporu</h1>
          <p className="text-sm text-gray-600 mt-1">Personellere zimmetli cihazları görüntüleyin</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtreler</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            label="Personel"
            value={personnelId}
            onChange={(value) => setPersonnelId(value)}
            options={[
              { value: 'all', label: 'Tüm Personeller' },
              ...personnel.map(p => ({ value: p.id, label: `${p.firstName} ${p.lastName}` }))
            ]}
            searchable={true}
          />

          <Select
            label="Departman"
            value={departmentId}
            onChange={(value) => setDepartmentId(value)}
            options={[
              { value: 'all', label: 'Tüm Departmanlar' },
              ...departments.map(d => ({ value: d.id, label: d.name }))
            ]}
            searchable={true}
          />

          <Select
            label="Şirket"
            value={companyId}
            onChange={(value) => setCompanyId(value)}
            options={[
              { value: 'all', label: 'Tüm Şirketler' },
              ...companies.map(c => ({ value: c.id, label: c.name }))
            ]}
            searchable={true}
          />
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={handleGenerateReport} disabled={isLoading}>
            {isLoading ? 'Rapor Oluşturuluyor...' : 'Rapor Oluştur'}
          </Button>
          <Button variant="outline" onClick={handleClearFilters}>
            Filtreleri Temizle
          </Button>
          {reportData && (
            <Button variant="secondary" onClick={handleExportPDF}>
              PDF Olarak Kaydet
            </Button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading && <Loading text="Rapor oluşturuluyor..." />}

      {/* Report Display */}
      {reportData && !isLoading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <PersonnelReport data={reportData} />
        </div>
      )}
    </div>
  );
};

export default PersonnelReportPage;
